import React, { useState, useEffect, useRef, useCallback } from 'react';

import {
  Upload, Search, Filter, GitBranch, Trash2, ChevronDown, ChevronUp,
  CheckSquare, Square, X, ArrowRight, Database, Eye, EyeOff,
  RefreshCw, Plus, Download, AlertCircle, Check
} from 'lucide-react';
import { crmApi } from '../services/api';
import './CRM.css';

// ── Column type detection ──────────────────────────────────────────────────────

function detectColumnType(values) {
  const nonEmpty = values.map(v => String(v ?? '').trim()).filter(v => v !== '');
  if (nonEmpty.length === 0) return 'text';

  // Boolean: only X, x, checkmarks, yes/no, blank
  const boolSet = new Set(['x', '✓', '✗', 'yes', 'no', 'true', 'false', '1', '0', '']);
  if (values.every(v => boolSet.has(String(v ?? '').toLowerCase().trim()))) return 'boolean';

  // Currency: starts with $ or ends with M/B/K as financial notation
  const currencyTest = v => /^\s*\$?\s*[\d,]+(\.\d+)?\s*[MBKmk]?\s*$/.test(String(v));
  if (nonEmpty.filter(v => v !== '').every(currencyTest)) {
    if (nonEmpty.some(v => String(v).includes('$') || /\d+\.\d/.test(String(v)))) return 'currency';
  }

  // Date
  const dateRegex = /^\d{1,2}[\/-]\d{1,2}[\/-]\d{2,4}$|^\d{4}-\d{2}-\d{2}$/;
  if (nonEmpty.every(v => dateRegex.test(v))) return 'date';

  // Numeric (after stripping $ , % M B K)
  const clean = v => String(v).replace(/[$,%\sMBKx]/gi, '').trim();
  if (nonEmpty.filter(v => clean(v) !== '').every(v => !isNaN(parseFloat(clean(v))))) return 'number';

  return 'text';
}

// Aliases for standard field detection
const FIELD_ALIASES = {
  name:        ['company', 'target', 'name', 'company name', 'seller', 'seller name', 'deal name',
                 'opportunity', 'code name', 'project', 'project name'],
  date:        ['date', 'end date', 'close date', 'closed date', 'transaction date', 'deal date',
                 'completion date', 'signed date'],
  value:       ['value', 'deal value', 'amount', 'price', 'purchase price', 'transaction value',
                 'enterprise value', 'ev', 'consideration'],
  revenue:     ['rev', 'revenue', 'ltm rev', 'ltm revenue', 'annual revenue', 'arr', 'revenue ($m)',
                 'rev.', 'sales'],
  ebitda:      ['ebitda', 'ltm ebitda', 'ebitda ($m)', 'operating income', 'ebitda margin'],
  stage:       ['stage', 'status', 'deal stage', 'phase', 'current stage'],
  owner:       ['owner', 'banker', 'advisor', 'banker / advisor', 'contact', 'assigned to', 'rep',
                 'deal lead', 'originator'],
  notes:       ['notes', 'note', 'description', 'comments', 'comment', 'detail'],
  industry:    ['industry', 'sector', 'vertical', 'business type', 'category'],
  closed:      ['closed', 'deal closed', 'transaction closed'],
};

function autoMapColumn(header) {
  const h = header.toLowerCase().trim();
  for (const [field, aliases] of Object.entries(FIELD_ALIASES)) {
    if (aliases.some(a => h === a || h.includes(a))) return field;
  }
  return 'custom';
}

// Parse currency value to a float (handles $335.0, $335M, 335.0, etc.)
function parseCurrencyValue(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return null;
  const mult = /[Mm]$/.test(s) ? 1_000_000 : /[Bb]$/.test(s) ? 1_000_000_000 : /[Kk]$/.test(s) ? 1_000 : 1;
  const clean = parseFloat(s.replace(/[$,MBKmk\s]/g, ''));
  return isNaN(clean) ? null : clean * mult;
}

function formatDisplayValue(value, colType) {
  if (value === null || value === undefined || value === '') return '';
  if (colType === 'boolean') {
    const v = String(value).toLowerCase().trim();
    return (v === 'x' || v === '1' || v === 'true' || v === 'yes' || v === '✓') ? '✓' : '';
  }
  if (colType === 'currency') {
    const num = parseCurrencyValue(value);
    if (num === null) return value;
    if (num >= 1_000_000_000) return `$${(num / 1_000_000_000).toFixed(1)}B`;
    if (num >= 1_000_000)     return `$${(num / 1_000_000).toFixed(1)}M`;
    if (num >= 1_000)         return `$${(num / 1_000).toFixed(0)}K`;
    return `$${num.toLocaleString()}`;
  }
  if (colType === 'date') {
    try { return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return value; }
  }
  return String(value);
}

// ── CSV Parser ────────────────────────────────────────────────────────────────
// Handles quoted fields, multi-row headers (grouped headers like the Eliassen sheet)

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  function splitRow(row) {
    const cells = []; let cur = ''; let inQ = false;
    for (let i = 0; i < row.length; i++) {
      const c = row[i];
      if (c === '"') { if (inQ && row[i+1] === '"') { cur += '"'; i++; } else inQ = !inQ; }
      else if (c === ',' && !inQ) { cells.push(cur); cur = ''; }
      else cur += c;
    }
    cells.push(cur);
    return cells.map(c => c.trim());
  }

  if (lines.length === 0) return { headers: [], rows: [] };

  const firstRow = splitRow(lines[0]);
  const secondRow = lines.length > 1 ? splitRow(lines[1]) : [];

  // Detect multi-row header: second row looks like headers (non-numeric, short strings)
  let headerRow;
  let dataStartIdx;
  const looksLikeHeader = row => row.filter(c => c).every(c =>
    isNaN(parseFloat(c.replace(/[$%,]/g, ''))) || c.length < 4
  );

  if (secondRow.length > 0 && looksLikeHeader(secondRow)) {
    // Merge: use "ParentHeader: ChildHeader" for grouped columns
    // Parent cells are spread across empties (merged cells export as empty after first)
    let lastParent = '';
    headerRow = firstRow.map((cell, i) => {
      if (cell) lastParent = cell;
      const child = secondRow[i] || '';
      if (child && cell) return `${cell}: ${child}`;
      if (child && !cell) return lastParent ? `${lastParent}: ${child}` : child;
      return cell || lastParent || `Column ${i + 1}`;
    });
    dataStartIdx = 2;
  } else {
    headerRow = firstRow;
    dataStartIdx = 1;
  }

  // Deduplicate headers
  const seen = {};
  headerRow = headerRow.map(h => {
    const base = h || 'Column';
    seen[base] = (seen[base] || 0) + 1;
    return seen[base] > 1 ? `${base} (${seen[base]})` : base;
  });

  const rows = lines.slice(dataStartIdx).map(line => {
    const cells = splitRow(line);
    const row = {};
    headerRow.forEach((h, i) => { row[h] = cells[i] ?? ''; });
    return row;
  }).filter(row => Object.values(row).some(v => v !== ''));

  return { headers: headerRow, rows };
}

// ── Column mapping step ───────────────────────────────────────────────────────

const STANDARD_FIELDS = [
  { value: 'name',     label: 'Company / Deal Name' },
  { value: 'date',     label: 'Date' },
  { value: 'value',    label: 'Deal / Purchase Value' },
  { value: 'revenue',  label: 'Revenue (LTM)' },
  { value: 'ebitda',   label: 'EBITDA (LTM)' },
  { value: 'stage',    label: 'Stage / Status' },
  { value: 'owner',    label: 'Owner / Banker / Advisor' },
  { value: 'notes',    label: 'Notes' },
  { value: 'industry', label: 'Industry / Vertical' },
  { value: 'custom',   label: 'Custom Field (keep as-is)' },
  { value: 'ignore',   label: 'Ignore this column' },
];

// ── Main CRM component ────────────────────────────────────────────────────────

const CRM = ({ deals: pipelineDeals = [], setDeals: setPipelineDeals, onNavigate }) => {
  const [view, setView] = useState('table'); // 'table' | 'import'
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Table state
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [hiddenCols, setHiddenCols] = useState(new Set());
  const [showColToggle, setShowColToggle] = useState(false);
  const [pushStage, setPushStage] = useState('level_1_assessment');
  const [pushLoading, setPushLoading] = useState(false);
  const [pushSuccess, setPushSuccess] = useState('');

  // Import state
  const [importStep, setImportStep] = useState('upload'); // 'upload' | 'map' | 'preview'
  const [parsedHeaders, setParsedHeaders] = useState([]);
  const [parsedRows, setParsedRows] = useState([]);
  const [colTypes, setColTypes] = useState({});
  const [colMappings, setColMappings] = useState({}); // header → standard field
  const [sourceName, setSourceName] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const fileInputRef = useRef(null);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await crmApi.list();
      setRecords(res.data || []);
    } catch {
      setError('Failed to load CRM records.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  // ── All unique column keys across all records (for table display) ──
  const allColumns = React.useMemo(() => {
    if (records.length === 0) return [];
    const reserved = new Set(['id','orgId','importedBy','importedAt','sourceName','columns',
      'inPipeline','pipelineDealId','_rowType']);
    const cols = new Set();
    records.forEach(r => Object.keys(r).forEach(k => { if (!reserved.has(k)) cols.add(k); }));
    // Put standard fields first
    const standard = ['name','date','stage','value','revenue','ebitda','owner','industry','notes'];
    const result = standard.filter(s => cols.has(s));
    cols.forEach(c => { if (!standard.includes(c)) result.push(c); });
    return result;
  }, [records]);

  // ── File handling ──────────────────────────────────────────────────────────
  const handleFile = (file) => {
    if (!file) return;
    setSourceName(file.name.replace(/\.[^.]+$/, ''));
    setImportError('');
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const { headers, rows } = parseCSV(e.target.result);
        if (headers.length === 0) { setImportError('Could not parse file. Make sure it is a valid CSV.'); return; }
        setParsedHeaders(headers);
        setParsedRows(rows);
        // Detect types from first 50 rows
        const sample = rows.slice(0, 50);
        const types = {};
        const mappings = {};
        headers.forEach(h => {
          const vals = sample.map(r => r[h]);
          types[h] = detectColumnType(vals);
          mappings[h] = autoMapColumn(h);
        });
        setColTypes(types);
        setColMappings(mappings);
        setImportStep('map');
      } catch (err) {
        setImportError('Failed to parse file: ' + err.message);
      }
    };
    reader.readAsText(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) handleFile(e.target.files[0]);
  };

  // ── Import confirm ─────────────────────────────────────────────────────────
  const handleImport = async () => {
    setImportLoading(true);
    setImportError('');
    try {
      // Build records from parsed rows + mappings
      const builtRecords = parsedRows.map(row => {
        const record = {};
        const customFields = {};
        parsedHeaders.forEach(h => {
          const mapping = colMappings[h];
          if (mapping === 'ignore') return;
          if (mapping === 'custom' || !mapping) {
            customFields[h] = row[h];
          } else {
            // Standard field — coerce value
            if (colTypes[h] === 'currency' || mapping === 'value' || mapping === 'revenue' || mapping === 'ebitda') {
              record[mapping] = parseCurrencyValue(row[h]);
            } else if (colTypes[h] === 'boolean') {
              const v = String(row[h] ?? '').toLowerCase().trim();
              record[mapping] = v === 'x' || v === '1' || v === 'true' || v === 'yes' || v === '✓';
            } else {
              record[mapping] = row[h];
            }
          }
        });
        // Merge custom fields in
        Object.assign(record, customFields);
        return record;
      }).filter(r => Object.values(r).some(v => v !== null && v !== undefined && v !== ''));

      const res = await crmApi.import(
        builtRecords,
        parsedHeaders.filter(h => colMappings[h] !== 'ignore'),
        sourceName
      );
      await fetchRecords();
      setView('table');
      setImportStep('upload');
      setParsedHeaders([]);
      setParsedRows([]);
    } catch (err) {
      setImportError(err.response?.data?.error || 'Import failed.');
    } finally {
      setImportLoading(false);
    }
  };

  // ── Push to Pipeline ───────────────────────────────────────────────────────
  const handlePushToPipeline = async () => {
    if (selectedIds.size === 0) return;
    setPushLoading(true);
    try {
      const res = await crmApi.pushToPipeline(
        Array.from(selectedIds),
        pushStage
      );
      if (res.data.deals?.length) {
        if (setPipelineDeals) {
          setPipelineDeals(prev => [...prev, ...res.data.deals]);
        }
      }
      await fetchRecords();
      setPushSuccess(`${res.data.deals?.length || 0} deal(s) added to Pipeline.`);
      setSelectedIds(new Set());
      setTimeout(() => setPushSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to push to pipeline.');
    } finally {
      setPushLoading(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (!window.confirm(`Delete ${selectedIds.size} record(s)?`)) return;
    for (const id of selectedIds) {
      await crmApi.delete(id).catch(() => {});
    }
    await fetchRecords();
    setSelectedIds(new Set());
  };

  // ── Table helpers ──────────────────────────────────────────────────────────
  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const filteredRecords = React.useMemo(() => {
    let list = [...records];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r => Object.values(r).some(v => String(v ?? '').toLowerCase().includes(q)));
    }
    if (sortCol) {
      list.sort((a, b) => {
        const av = String(a[sortCol] ?? '').toLowerCase();
        const bv = String(b[sortCol] ?? '').toLowerCase();
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      });
    }
    return list;
  }, [records, search, sortCol, sortDir]);

  const visibleCols = allColumns.filter(c => !hiddenCols.has(c));

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredRecords.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredRecords.map(r => r.id)));
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ── Render: Import flow ────────────────────────────────────────────────────
  if (view === 'import') {
    return (
      <div className="crm-page">
        <div className="crm-header">
          <button className="crm-back-btn" onClick={() => { setView('table'); setImportStep('upload'); }}>
            <X size={16} /> Cancel Import
          </button>
          <h2>Import Deal Data</h2>
        </div>

        <div className="crm-import-steps">
          {['Upload File', 'Map Columns', 'Preview & Import'].map((label, i) => {
            const stepKey = ['upload','map','preview'][i];
            const current = ['upload','map','preview'].indexOf(importStep);
            return (
              <React.Fragment key={label}>
                <div className={`crm-import-step ${current === i ? 'active' : current > i ? 'done' : ''}`}>
                  <span className="crm-step-dot">{current > i ? <Check size={12} /> : i + 1}</span>
                  <span>{label}</span>
                </div>
                {i < 2 && <div className={`crm-step-line ${current > i ? 'done' : ''}`} />}
              </React.Fragment>
            );
          })}
        </div>

        {importError && <div className="crm-import-error"><AlertCircle size={15} />{importError}</div>}

        {/* Step 1: Upload */}
        {importStep === 'upload' && (
          <div className="crm-upload-area">
            <div
              className={`crm-dropzone ${dragOver ? 'drag-over' : ''}`}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload size={36} />
              <h3>Drag &amp; drop your file here</h3>
              <p>Supports CSV files exported from Excel, Google Sheets, Salesforce, or HubSpot.</p>
              <p className="crm-drop-hint">Files with grouped/multi-row headers are handled automatically.</p>
              <button className="crm-browse-btn">Browse File</button>
              <input ref={fileInputRef} type="file" accept=".csv,.txt" style={{ display: 'none' }}
                onChange={handleFileChange} />
            </div>

            <div className="crm-import-tips">
              <h4>What DealNexa handles automatically:</h4>
              <ul>
                <li><Check size={13} /> <strong>Multi-row headers</strong> — grouped column names like "LTM Financials: Rev." and "Eliassen Participation Level: NDA/CIM"</li>
                <li><Check size={13} /> <strong>Currency values</strong> — $335.0, $335M, $1.2B all parsed correctly</li>
                <li><Check size={13} /> <strong>Checkbox columns</strong> — "X" marks (participation stages, flags) detected as boolean</li>
                <li><Check size={13} /> <strong>EBITDA multiples</strong> — numeric columns like "Rev. Multiple", "EBITDA Multiple" preserved</li>
                <li><Check size={13} /> <strong>Custom columns</strong> — any column we don't recognize becomes a searchable custom field</li>
                <li><Check size={13} /> <strong>Salesforce &amp; HubSpot exports</strong> — standard field names auto-mapped</li>
              </ul>
            </div>
          </div>
        )}

        {/* Step 2: Map columns */}
        {importStep === 'map' && (
          <div className="crm-map-area">
            <div className="crm-map-header">
              <div>
                <h3>Map your columns</h3>
                <p>DealNexa detected {parsedHeaders.length} columns and {parsedRows.length} rows.
                  Review the auto-detected mappings and adjust as needed.</p>
              </div>
              <div className="crm-source-name">
                <label>Import name</label>
                <input value={sourceName} onChange={e => setSourceName(e.target.value)}
                  placeholder="e.g. Q1 2025 Pipeline" />
              </div>
            </div>

            <div className="crm-map-table-wrap">
              <table className="crm-map-table">
                <thead>
                  <tr>
                    <th>Column from your file</th>
                    <th>Detected type</th>
                    <th>Sample values</th>
                    <th>Map to DealNexa field</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedHeaders.map(h => (
                    <tr key={h} className={colMappings[h] === 'ignore' ? 'crm-map-row-ignored' : ''}>
                      <td className="crm-col-name">{h}</td>
                      <td>
                        <span className={`crm-type-badge ${colTypes[h]}`}>{colTypes[h]}</span>
                      </td>
                      <td className="crm-sample-vals">
                        {parsedRows.slice(0, 3).map(r => r[h]).filter(v => v).join(' · ') || '—'}
                      </td>
                      <td>
                        <select
                          value={colMappings[h] || 'custom'}
                          onChange={e => setColMappings(prev => ({ ...prev, [h]: e.target.value }))}
                          className="crm-map-select"
                        >
                          {STANDARD_FIELDS.map(f => (
                            <option key={f.value} value={f.value}>{f.label}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="crm-map-actions">
              <button className="crm-btn-secondary" onClick={() => setImportStep('upload')}>Back</button>
              <button className="crm-btn-primary" onClick={() => setImportStep('preview')}>
                Preview Import <ArrowRight size={15} />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Preview */}
        {importStep === 'preview' && (
          <div className="crm-preview-area">
            <div className="crm-preview-header">
              <h3>Preview — {parsedRows.length} records from "{sourceName}"</h3>
              <p>First 10 rows shown below. Review before importing.</p>
            </div>

            <div className="crm-preview-table-wrap">
              <table className="crm-preview-table">
                <thead>
                  <tr>
                    {parsedHeaders.filter(h => colMappings[h] !== 'ignore').map(h => (
                      <th key={h}>
                        <span className="crm-preview-orig">{h}</span>
                        {colMappings[h] && colMappings[h] !== 'custom' && (
                          <span className="crm-preview-mapped">→ {colMappings[h]}</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parsedRows.slice(0, 10).map((row, i) => (
                    <tr key={i}>
                      {parsedHeaders.filter(h => colMappings[h] !== 'ignore').map(h => (
                        <td key={h}>{formatDisplayValue(row[h], colTypes[h])}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="crm-map-actions">
              <button className="crm-btn-secondary" onClick={() => setImportStep('map')}>Back</button>
              <button className="crm-btn-primary" onClick={handleImport} disabled={importLoading}>
                {importLoading ? 'Importing...' : `Import ${parsedRows.length} Records`}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Render: Table view ─────────────────────────────────────────────────────
  return (
    <div className="crm-page">
      {/* Header */}
      <div className="crm-header">
        <div className="crm-title-row">
          <Database size={22} />
          <div>
            <h1>CRM</h1>
            <span className="crm-subtitle">{records.length} total records</span>
          </div>
        </div>
        <div className="crm-header-actions">
          <button className="crm-btn-secondary" onClick={() => { setView('import'); setImportStep('upload'); }}>
            <Upload size={15} /> Import Data
          </button>
          {onNavigate && (
            <button className="crm-btn-secondary" onClick={() => onNavigate('pipeline')}>
              <GitBranch size={15} /> View Pipeline
            </button>
          )}
        </div>
      </div>

      {error && <div className="crm-error"><AlertCircle size={15} />{error}</div>}
      {pushSuccess && <div className="crm-success"><Check size={15} />{pushSuccess}</div>}

      {/* Toolbar */}
      <div className="crm-toolbar">
        <div className="crm-search-wrap">
          <Search size={15} className="crm-search-icon" />
          <input
            type="text"
            placeholder="Search all fields..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="crm-search-input"
          />
          {search && <button className="crm-search-clear" onClick={() => setSearch('')}><X size={13} /></button>}
        </div>

        <div className="crm-toolbar-right">
          {selectedIds.size > 0 && (
            <div className="crm-bulk-actions">
              <span className="crm-selected-count">{selectedIds.size} selected</span>
              <select
                value={pushStage}
                onChange={e => setPushStage(e.target.value)}
                className="crm-stage-select"
              >
                <option value="level_1_assessment">Level 1 Assessment</option>
                <option value="level_2_assessment">Level 2 Assessment</option>
                <option value="loi">LOI</option>
                <option value="final_agreements">Final Agreements</option>
                <option value="closed">Closed</option>
              </select>
              <button className="crm-btn-pipeline" onClick={handlePushToPipeline} disabled={pushLoading}>
                <GitBranch size={14} /> {pushLoading ? 'Adding...' : 'Add to Pipeline'}
              </button>
              <button className="crm-btn-danger-sm" onClick={handleDeleteSelected}>
                <Trash2 size={14} />
              </button>
            </div>
          )}

          <div className="crm-col-toggle-wrap">
            <button className="crm-btn-icon" onClick={() => setShowColToggle(v => !v)} title="Toggle columns">
              <Filter size={15} />
            </button>
            {showColToggle && (
              <div className="crm-col-toggle-dropdown">
                <p className="crm-col-toggle-title">Show / Hide Columns</p>
                {allColumns.map(col => (
                  <label key={col} className="crm-col-toggle-row">
                    <input
                      type="checkbox"
                      checked={!hiddenCols.has(col)}
                      onChange={() => setHiddenCols(prev => {
                        const next = new Set(prev);
                        next.has(col) ? next.delete(col) : next.add(col);
                        return next;
                      })}
                    />
                    <span>{col}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <button className="crm-btn-icon" onClick={fetchRecords} title="Refresh">
            <RefreshCw size={15} />
          </button>
        </div>
      </div>

      {/* Empty state */}
      {!loading && records.length === 0 && (
        <div className="crm-empty">
          <Database size={40} />
          <h3>No CRM records yet</h3>
          <p>Import your deal database from Excel, Google Sheets, Salesforce, or HubSpot.</p>
          <button className="crm-btn-primary" onClick={() => { setView('import'); setImportStep('upload'); }}>
            <Upload size={15} /> Import Your First Dataset
          </button>
        </div>
      )}

      {/* Table */}
      {!loading && records.length > 0 && (
        <div className="crm-table-wrap">
          <table className="crm-table">
            <thead>
              <tr>
                <th className="crm-th-check">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === filteredRecords.length && filteredRecords.length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                {visibleCols.map(col => (
                  <th key={col} onClick={() => toggleSort(col)} className="crm-th-sortable">
                    <span>{col}</span>
                    {sortCol === col && (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                  </th>
                ))}
                <th className="crm-th-status">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.map(record => {
                // Determine the column type for each column (from imported column metadata)
                const colTypesForRecord = {};
                if (record.columns) {
                  record.columns.forEach(h => {
                    colTypesForRecord[h] = detectColumnType(records.map(r => r[h]));
                  });
                }
                return (
                  <tr key={record.id} className={selectedIds.has(record.id) ? 'crm-row-selected' : ''}>
                    <td className="crm-td-check">
                      <input type="checkbox" checked={selectedIds.has(record.id)} onChange={() => toggleSelect(record.id)} />
                    </td>
                    {visibleCols.map(col => {
                      const val = record[col];
                      const colType = colTypesForRecord[col] || detectColumnType([val]);
                      const display = formatDisplayValue(val, colType);
                      return (
                        <td key={col} className={`crm-td ${colType === 'boolean' ? 'crm-td-bool' : ''}`}>
                          {colType === 'boolean'
                            ? (display === '✓' ? <span className="crm-bool-check">✓</span> : '')
                            : display}
                        </td>
                      );
                    })}
                    <td className="crm-td-status">
                      {record.inPipeline
                        ? <span className="crm-badge-pipeline"><GitBranch size={11} /> In Pipeline</span>
                        : <span className="crm-badge-crm">CRM Only</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="crm-table-footer">
            <span>{filteredRecords.length} of {records.length} records</span>
            {search && <span>· filtered by "{search}"</span>}
          </div>
        </div>
      )}

      {loading && <div className="crm-loading">Loading CRM records...</div>}
    </div>
  );
};

export default CRM;
